// features/workstations/home.js
// ═══════════════════════════════════════════════════════════
// WORKSTATION HOME — command-center landing page
//
// What the user sees when they click the Workstation icon in
// the sidebar. Shows:
//   - Topbar: "Workstations" + date stamp
//   - Header block: greeting + subtitle
//   - 2-column card grid of user's Workstations
//   - + New Workstation tile
//   - Recent Activity row across all Workstations
//
// Data source: GET /api/workstations/list?user_id=...
// Refresh: on mount + when parent re-mounts the feature
// ═══════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import DomainCard from './components/domain-card';
import AddWorkstationCard from './components/add-workstation-card';
import RecentActivity from './components/recent-activity';
import EmptyState from './components/empty-state';
import MorningBriefCard from './components/morning-brief-card';

export default function WorkstationHome({
  userId,
  userRecord,
  onOpenWorkstation,
  onCreateNew,
}) {
  const [workstations, setWorkstations] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const qs = userId ? `?user_id=${encodeURIComponent(userId)}` : '';
        const res = await fetch(`/api/workstations/list${qs}`, { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setWorkstations(data.workstations || []);
        setRecentActivity(data.recent_activity || []);
      } catch (err) {
        console.warn('[workstation-home] load failed:', err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const firstName = userRecord?.first_name || userRecord?.username || null;
  const dateString = new Date().toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
  });

  return (
    <div className="ws-home-root">
      <div className="ws-home-topbar">
        <div className="ws-home-title">Workstations</div>
        <div className="ws-home-subtitle">Build, learn, create — your configured workspaces.</div>
        <div className="ws-home-date">{dateString}</div>
      </div>

      <div className="ws-home-content">
        {loading ? (
          <div className="ws-loading">Loading workstations…</div>
        ) : workstations.length === 0 ? (
          <EmptyState onCreate={onCreateNew} />
        ) : (
          <>
            <div className="ws-home-header">
              <div className="ws-home-eyebrow">
                {firstName ? `Good morning, ${firstName}` : 'Good morning'}
              </div>
              <div className="ws-home-h1">Your Workstations</div>
              <div className="ws-home-h1-sub">
                {workstations.length} workstation{workstations.length === 1 ? '' : 's'} ·
                Each one is a configured workspace for a specific kind of work.
              </div>
            </div>

            {/* Today's Brief — daily synthesis from the briefing
                cron, only renders when one exists for today. */}
            {workstations.some((w) => w.domain_key === 'trading') && (
              <MorningBriefCard domain="trading" />
            )}

            <div className="ws-card-grid">
              {workstations.map(ws => (
                <DomainCard
                  key={ws.id}
                  workstation={ws}
                  onOpen={onOpenWorkstation}
                />
              ))}
              <AddWorkstationCard onClick={onCreateNew} />
            </div>

            {recentActivity.length > 0 && (
              <RecentActivity events={recentActivity} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
